import { computed, linkedSignal, Signal, signal, WritableSignal } from '@angular/core';
import { AppliedColumn, AppliedColumnSchema, ColumnDef } from '@shared/dataview/core/dataview-types';
import { isEqual, uniq } from 'lodash';

export interface DataViewColumnsStoreOptions {
  available: Signal<ColumnDef[]>;
  projection?: 'columns' | 'all';
  alwaysFields?: string[];
  favouriteIds?: Signal<string[]>;
}

export class DataViewColumnsStore {
  readonly available: Signal<ColumnDef[]>;
  readonly applied = signal<AppliedColumn[]>([]);
  readonly gridColumns: Signal<AppliedColumn[]>;
  readonly favouriteIds: WritableSignal<string[]>;
  readonly fields: Signal<string[] | undefined>;
  readonly exportFields: Signal<string[]>;

  private readonly availableById: Signal<ReadonlyMap<string, ColumnDef>>;

  constructor(options: DataViewColumnsStoreOptions) {
    this.available = options.available;
    this.availableById = computed(() => new Map(this.available().map(column => [column.id, column])));
    this.favouriteIds = linkedSignal(() => options.favouriteIds?.() ?? []);

    this.gridColumns = computed(() => this.applied().map(column => ({ ...column })), {
      // app-data-view-grid resizes the columns it is given in place, so columns that already match are kept as they are.
      equal: (shown, next) => isEqual(shown.map(toColumnSchema), next.map(toColumnSchema)),
    });

    this.fields = computed(
      () => {
        if (options.projection !== 'columns') return undefined;
        const fromColumns = this.applied().flatMap(column => column.fields ?? (column.field ? [column.field] : []));
        return uniq([...fromColumns, ...(options.alwaysFields ?? [])]);
      },
      { equal: isEqual },
    );

    this.exportFields = computed(
      () =>
        this.applied().flatMap(column => {
          if (column.exportAsFields) return column.exportAsFields;
          return column.field ? [column.field] : [];
        }),
      { equal: isEqual },
    );
  }

  applyFromSchema(schema: AppliedColumnSchema[]): void {
    this.applied.set(this.resolve(schema));
  }

  commitGridWidths(): void {
    this.applied.set(this.gridColumns().map(column => ({ ...column })));
  }

  canonicalize(schema: AppliedColumnSchema[]): AppliedColumnSchema[] {
    return this.resolve(schema).map(toColumnSchema);
  }

  toSchema(): AppliedColumnSchema[] {
    return this.applied().map(toColumnSchema);
  }

  private resolve(schema: AppliedColumnSchema[]): AppliedColumn[] {
    const byId = this.availableById();
    const resolved: AppliedColumn[] = [];

    for (const entry of schema) {
      const def = byId.get(entry.id);
      if (def) resolved.push({ ...def, width: entry.width });
    }

    return resolved;
  }
}

function toColumnSchema(column: AppliedColumn): AppliedColumnSchema {
  return column.width === undefined ? { id: column.id } : { id: column.id, width: column.width };
}
